import { DateValue } from '@internationalized/date';
import { Color, Spacing } from '@persona-id/design-tokens';
import { SlidersIcon } from '@persona-id/design-tokens/icons';
import { RangeValue } from '@react-types/shared';
import { FC, useMemo } from 'react';
import styled from 'styled-components';

import {
  optionWithSubtextMapper,
  SelectOptionValue,
  useLabelTextMapper,
} from 'dashboard/components-lib/MultiSelect/MultiSelect';
import { FilterConfig } from 'dashboard/components-lib/TableControls';
import TableControls from 'dashboard/components-lib/TableControls/TableControls';
import { Workflow } from 'dashboard/models/workflow';
import { WorkflowRunStatus } from 'dashboard/models/workflow-run';
import { WorkflowVersion } from 'dashboard/models/workflow-version';
import { WorkflowRunsFilterParams } from 'dashboard/redux/actions/workflow-runs';
import { Z_INDICES } from 'dashboard/styles/variables';
import Button from 'design-system/components/Button/Button';
import Card from 'design-system/components/Card';
import Popover from 'design-system/components/Popover/Popover';
import Spacer from 'design-system/components/Spacer';
import { Heading } from 'design-system/components/Text';
import ToggleSlider from 'design-system/components/ToggleSlider';

const STATUS_FILTER_OPTIONS = [
  {
    label: 'Completed',
    value: WorkflowRunStatus.Completed,
  },
  {
    label: 'Errored',
    value: WorkflowRunStatus.Errored,
  },
  {
    label: 'In Progress',
    value: WorkflowRunStatus.InProgress,
  },
];

const PopoverCard = styled(Card)`
  width: 300px;
  padding: ${Spacing[4]};
`;

const ItemWrapper = styled('div')`
  background: ${Color.BackgroundPrimary};
  width: 100%;
  border-bottom: 1px solid ${({ theme }) => theme.color.border.secondary};
  padding: ${Spacing[2]} ${Spacing[2]};

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: left;
`;

const SuffixContainer = styled('div')`
  padding-left: ${Spacing[2]};
  margin-left: auto;
  align-self: center;
`;

const TriggerIconButton = (props: { 'aria-label'?: string }) => {
  return (
    <Button width='icon' variant='basic' {...props}>
      <SlidersIcon />
    </Button>
  );
};

interface WorkflowRunsConfigurationDropdownProps {
  columns: string[];
  handleColumnUpdate: (columnIds: string[]) => void;
  columnConfigOptions: SelectOptionValue<string>[];
}

const WorkflowRunsConfigurationDropdown: FC<WorkflowRunsConfigurationDropdownProps> = ({
  columns,
  handleColumnUpdate,
  columnConfigOptions,
}) => {
  return (
    <Popover>
      <Popover.Trigger>
        <TriggerIconButton aria-label='Configure table' />
      </Popover.Trigger>
      <Popover.Content
        style={{ zIndex: Z_INDICES.dropdown }}
        sideOffset={8}
        side='bottom'
        align='end'
      >
        <PopoverCard elevated>
          <Heading size='sm'>Configure columns</Heading>
          <Spacer height={2} />
          {columnConfigOptions.map((column) => (
            <ItemWrapper key={column.value}>
              <span>{column.label}</span>
              <SuffixContainer>
                <ToggleSlider
                  data-test={`toggle-column-${column.value}`}
                  value={columns.includes(column.value)}
                  onChange={() =>
                    handleColumnUpdate(
                      columns.includes(column.value)
                        ? columns.filter((c) => c !== column.value)
                        : [...columns, column.value],
                    )
                  }
                />
              </SuffixContainer>
            </ItemWrapper>
          ))}
        </PopoverCard>
      </Popover.Content>
    </Popover>
  );
};

interface WorkflowRunsConfigurationBarProps {
  workflowId: string | null;
  workflowsMap: Record<string, Workflow>;
  workflowRunFilters?: WorkflowRunsFilterParams;
  workflowVersions: WorkflowVersion[];
  isLoadingWorkflowVersions: boolean;
  columnConfigOptions: SelectOptionValue<string>[];
  columns: string[];
  handleWorkflowIdFilterUpdate: (workflowIds: string[]) => void;
  handleVersionFilterUpdate: (versionIds: string[]) => void;
  onLoadMoreVersions?: () => void;
  handleStatusFilterUpdate: (status: WorkflowRunStatus[]) => void;
  handleOmniFilterUpdate: (omni: string) => void;
  handleColumnUpdate: (columnIds: string[]) => void;
  handleDateUpdate: (range: RangeValue<DateValue> | null) => void;
}

const WorkflowRunsConfigurationBar: FC<WorkflowRunsConfigurationBarProps> = ({
  workflowId,
  workflowsMap,
  workflowRunFilters,
  workflowVersions,
  columnConfigOptions,
  columns,
  handleWorkflowIdFilterUpdate,
  handleVersionFilterUpdate,
  onLoadMoreVersions,
  handleStatusFilterUpdate,
  handleColumnUpdate,
  handleOmniFilterUpdate,
  handleDateUpdate,
}) => {
  const workflowVersionOptions = useMemo(() => {
    if (workflowVersions == null) {
      return [];
    }
    return workflowVersions.map((version) => ({
      label: version.id,
      value: version.id,
    }));
  }, [workflowVersions]);

  const workflowOptions: SelectOptionValue<string>[] = useMemo(
    () =>
      workflowsMap
        ? Object.keys(workflowsMap)
            .filter((id) => workflowsMap[id].attributes.active)
            .map((id) => ({
              label: `${workflowsMap[id].attributes.name} (${id})`,
              customLabel: optionWithSubtextMapper(workflowsMap[id].attributes.name, id),
              value: id,
            }))
        : [{ label: workflowId ?? 'Loading...', value: workflowId ?? 'loading' }],
    [workflowsMap, workflowId],
  );

  const versionIdsFilterLabelTextMapper = useLabelTextMapper(workflowVersionOptions, 'Versions', {
    emptyIsAll: true,
  });

  const workflowIdFilterLabelTextMapper = useLabelTextMapper(workflowOptions, 'Workflows', {
    emptyIsAll: true,
  });

  const filterConfig: FilterConfig[] = [
    ...(!workflowId
      ? [
          {
            id: 'workflow',
            type: 'multiselect-legacy' as const,
            name: 'Workflow',
            selectOptionValues: workflowOptions,
            value: workflowRunFilters?.workflowIds ?? [],
            onChange: handleWorkflowIdFilterUpdate,
            labelTextMapper: workflowIdFilterLabelTextMapper,
            searchEnabled: true,
          },
        ]
      : []),
    {
      id: 'version',
      type: 'multiselect-legacy',
      name: 'Version',
      selectOptionValues: workflowVersionOptions,
      value: workflowRunFilters?.workflowVersionIds ?? [],
      onChange: handleVersionFilterUpdate,
      onLoadMore: onLoadMoreVersions,
      labelTextMapper: versionIdsFilterLabelTextMapper,
    },
    {
      id: 'status',
      type: 'multiselect',
      name: 'Status',
      options: STATUS_FILTER_OPTIONS,
      value: workflowRunFilters?.status ?? [],
      onChange: (stauses) => handleStatusFilterUpdate(stauses as WorkflowRunStatus[]),
    },
    {
      id: 'createdAt',
      type: 'daterange',
      name: 'Dates completed',
      value: {
        startDate: workflowRunFilters?.completedAtStart,
        endDate: workflowRunFilters?.completedAtEnd,
      },
      onChange: handleDateUpdate,
    },
  ];

  return (
    <TableControls
      searchProps={{
        id: 'users-table__search-input',
        handleInput: handleOmniFilterUpdate,
        value: workflowRunFilters?.omni ?? '',
        placeholder: 'Search by Workflow Run ID or trigger object ID',
      }}
      filters={filterConfig}
      propertiesButton={
        <WorkflowRunsConfigurationDropdown
          columnConfigOptions={columnConfigOptions}
          columns={columns}
          handleColumnUpdate={handleColumnUpdate}
        />
      }
    />
  );
};

export default WorkflowRunsConfigurationBar;
